import { useAuthStore } from '@/stores/authStore'

const TIER_LABELS: Record<number, string> = {
  0: 'Free',
  1: 'Growth',
  2: 'Pro',
  3: 'Infrastructure',
}

// Handle legacy string tiers from pre-migration login sessions
const STRING_TIER_MAP: Record<string, number> = {
  free: 0, growth: 1, pro: 2, enterprise: 3, infrastructure: 3,
}

function resolveTier(tier: number | string | undefined): number {
  if (tier === undefined || tier === null) return 0
  if (typeof tier === 'number') return tier
  return STRING_TIER_MAP[tier.toLowerCase()] ?? 0
}

interface FeatureGateProps {
  requiredTier: number // 0=Free, 1=Growth, 2=Pro, 3=Infrastructure
  title?: string
  description?: string
  children: React.ReactNode
}

export default function FeatureGate({ requiredTier, title, description, children }: FeatureGateProps) {
  const { user } = useAuthStore()
  const vendorTier = resolveTier(user?.subscriptionTier)

  if (vendorTier >= requiredTier) {
    return <>{children}</>
  }

  const tierLabel = TIER_LABELS[requiredTier] || `Tier ${requiredTier}`

  return (
    <div className="card" style={{ textAlign: 'center', padding: '2.5rem 2rem', background: 'var(--color-gray-50)', border: '1px dashed var(--color-gray-300)' }}>
      <div style={{ fontSize: '2rem', marginBottom: '0.75rem', opacity: 0.3 }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', color: 'var(--color-gray-400)' }}>
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
      </div>
      {title && (
        <h3 style={{ fontSize: 'var(--text-base)', color: 'var(--color-gray-700)', fontWeight: 'var(--font-weight-semibold)', margin: '0 0 0.5rem' }}>
          {title}
        </h3>
      )}
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', margin: '0 0 1.25rem', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
        {description || `Upgrade to ${tierLabel} to unlock this feature.`}
      </p>
      <a
        href="https://tixxly.ai/pricing"
        style={{
          display: 'inline-block',
          padding: '0.5rem 1.5rem',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-pure-white)',
          background: 'var(--color-tixxly-navy)',
          borderRadius: '6px',
          textDecoration: 'none',
        }}
      >
        Upgrade to {tierLabel}
      </a>
    </div>
  )
}
